import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { PrimaryTrack } from '../../utils/formNames';
import { getFormData } from '../../utils/sessionStorage';
import { FORM_HOST, TrackValues } from '../../utils/constants';
import EnrollmentForm from './EnrollmentForm';
import EnrollmentFormRadioListInput from '../FormInputs/FormRadioListInput';
import ControlledFormSelectInput from '../FormInputs/ControlledFormSelectInput';
import EnrollmentFormError from '../FormInputs/FormError';

const formName = PrimaryTrack;
const pardotFormUrl = `${FORM_HOST}/l/772833/2020-04-09/58vzmb`;
const callbackLocations = {
  success_location: `${FORM_HOST}/l/772833/2020-07-06/7k97ls/772833/58609/primary_track_success.js`,
  error_location: `${FORM_HOST}/l/772833/2020-07-06/7k97lq/772833/58607/primary_track_error.js`,
};

const PrimaryTrackForm = ({ setPrimaryTrack }) => {
  const { register, setValue, handleSubmit, triggerValidation, formState, errors } = useForm({
    mode: 'onChange',
    submitFocusError: false,
  });

  const previousData = getFormData(formName);

  const preSubmit = (data) => {
    if (data.primary_track) {
      setPrimaryTrack(data.primary_track);
    }
  };

  // Necessary because react-hook-form has a bug
  // regarding default values on required radio inputs
  useEffect(() => {
    if (previousData.primary_track) {
      triggerValidation();
    }
  }, [previousData.primary_track, triggerValidation]);

  const {
    SWE,
    WebDev,
    Data,
    Design,
    PSO,
    Marketing,
    Sales,
    ITCybersecurity,
    FinanceBankingAccounting,
    OperationsManagement,
    ProjectProgramManagement,
    SupplyChain,
    StrategyConsulting,
    PeopleTalentHR,
    EngineeringNonSoftware,
  } = TrackValues;

  const moreTrackOptions = [
    { label: 'IT & Cybersecurity', value: ITCybersecurity },
    { label: 'Civil, Mechanical, and Other Engineering', value: EngineeringNonSoftware },
    { label: 'Project & Program Management', value: ProjectProgramManagement },
    { label: 'Operations Management', value: OperationsManagement },
    { label: 'Strategy & Consulting', value: StrategyConsulting },
    { label: 'Supply Chain', value: SupplyChain },
    { label: 'Finance, Banking & Accounting', value: FinanceBankingAccounting },
    { label: 'People, Talent & HR', value: PeopleTalentHR },
  ];

  const [moreTracksVal, setMoreTracksVal] = useState(previousData.prospect_form_role_type || '');

  return (
    <EnrollmentForm
      formName={formName}
      callbackLocations={callbackLocations}
      actionUrl={pardotFormUrl}
      formValid={formState.isValid}
      validationHandler={handleSubmit}
      preSubmit={preSubmit}
    >
      <legend>Track Selection</legend>
      <p>
        What kind of job are you looking for? <em>*</em>
      </p>
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="swe-track"
        label="Software Engineering"
        value={SWE}
        defaultValue={previousData.primary_track}
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="web-track"
        label="Web Development"
        value={WebDev}
        defaultValue={previousData.primary_track}
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="data-track"
        label="Data Science"
        value={Data}
        defaultValue={previousData.primary_track}
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="design-track"
        label="UI/UX Design"
        value={Design}
        defaultValue={previousData.primary_track}
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="product-track"
        label="Product Management"
        value={PSO}
        defaultValue={previousData.prospect_form_role_type}
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="marketing-track"
        label="Marketing"
        value={Marketing}
        defaultValue={previousData.primary_track}
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="sales-track"
        label="Sales"
        value={Sales}
        defaultValue={previousData.primary_track}
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="it-cybersecurity-track"
        label="IT & Cybersecurity"
        value={ITCybersecurity}
        defaultValue={previousData.prospect_form_role_type}
        hidden
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="engineering-non-software-track"
        label="Civil, Mechanical, and Other Engineering"
        value={EngineeringNonSoftware}
        defaultValue={previousData.prospect_form_role_type}
        hidden
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="project-program-management"
        label="Project & Program Management"
        value={ProjectProgramManagement}
        defaultValue={previousData.prospect_form_role_type}
        hidden
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="operations-management"
        label="Operations Management"
        value={OperationsManagement}
        defaultValue={previousData.prospect_form_role_type}
        hidden
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="strategy-consulting"
        label="Strategy & Consulting"
        value={StrategyConsulting}
        defaultValue={previousData.prospect_form_role_type}
        hidden
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="supply-chain"
        label="Supply Chain"
        value={SupplyChain}
        defaultValue={previousData.prospect_form_role_type}
        hidden
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="operations-management"
        label="Finance, Banking & Accounting"
        value={FinanceBankingAccounting}
        defaultValue={previousData.prospect_form_role_type}
        hidden
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <EnrollmentFormRadioListInput
        name="primary_track"
        id="people-talent-hr"
        label="People, Talent & HR"
        value={PeopleTalentHR}
        defaultValue={previousData.prospect_form_role_type}
        hidden
        inputRef={register({ required: true })}
        onClick={() => setMoreTracksVal('')}
      />
      <ControlledFormSelectInput
        onChange={(e) => {
          setValue('primary_track', e.target.value, true);
          setMoreTracksVal(e.target.value);
        }}
        name="more_tracks_select"
        options={moreTrackOptions}
        value={moreTracksVal}
        placeholder="MORE TRACKS"
        style={{
          marginTop: '12px',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='8' viewBox='0 0 10 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.19575 0H1.80425C1.01881 0 0.539969 0.86395 0.956249 1.53L4.152 6.6432C4.54367 7.26987 5.45633 7.26987 5.848 6.6432L9.04375 1.53C9.46003 0.86395 8.98119 0 8.19575 0Z' fill='%23e8ebf0'/%3E%3C/svg%3E%0A")`,
        }}
        selectedStyle={{
          border: '1px solid #535fe5',
          backgroundColor: '#e0e5ff',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='8' viewBox='0 0 10 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.19575 0H1.80425C1.01881 0 0.539969 0.86395 0.956249 1.53L4.152 6.6432C4.54367 7.26987 5.45633 7.26987 5.848 6.6432L9.04375 1.53C9.46003 0.86395 8.98119 0 8.19575 0Z' fill='%23535FE5'/%3E%3C/svg%3E%0A")`,
          color: '#535fe5',
        }}
      />
      <EnrollmentFormError
        errorField={errors.primary_track}
        errorText={{ required: 'Please select a career path' }}
        blockError
      />
    </EnrollmentForm>
  );
};

PrimaryTrackForm.propTypes = {
  setPrimaryTrack: PropTypes.func.isRequired,
};

export default PrimaryTrackForm;
